<template>
  <span
    class="syndications-popper__container"
    data-tesid="slot"
  >
    <slot :toggle-open="handleToggle" />
  </span>
  <Popper
    v-model="isOpen"
    v-bind="popperAttrs"
  >
    <SyndicationsTable
      sort-by="source_name"
      :syndications="syndications"
      @syndication-clicked="syndicationClicked"
    >
      <template #header>
        <th class="tw:w-full tw:px-4 tw:pb-3 tw:pt-3.5 tw:text-left">
          {{ getLocaleText("syndications.source") }}
        </th>
        <th class="tw:px-4">
          <BaseIcon
            :aria-label="getLocaleText('syndications.followers')"
            class="tw:text-2xl tw:font-normal"
            icon="social-group"
          />
        </th>
      </template>

      <template #body="{ syndicated }">
        <td class="tw:px-4 tw:py-1.5 tw:text-left">
          <span
            v-if="!label(syndicated).isHashtag"
            class="tw:line-clamp-1 tw:font-medium tw:text-denim-900"
            data-testid="source-name"
          >
            {{ syndicated.source_name }}
          </span>
          <span
            v-if="!hideHandle"
            :class="[
              'tw:line-clamp-1',
              label(syndicated).isHashtag
                ? 'tw:font-medium tw:text-denim-900'
                : 'tw:text-denim-600',
            ]"
            data-testid="handle"
            :title="label(syndicated).handle"
          >
            {{ label(syndicated).handle }}
          </span>
        </td>
        <td>
          <span
            v-if="syndicated.follower_count"
            class="tw:px-4 tw:text-xs tw:font-medium"
          >
            {{ getLocaleNumber(syndicated.follower_count, "suffix") }}
          </span>

          <BaseIcon
            v-else
            class="tw:text-2xl tw:text-denim-500"
            icon="minus"
          />
        </td>
      </template>
    </SyndicationsTable>
  </Popper>
</template>

<script setup lang="ts">
import { getLocaleNumber, getLocaleText } from "shared/boot/i18n";
import BaseIcon from "shared/components/base/BaseIcon";
import Popper from "shared/components/base/BasePopper";
import { SOCIAL_PREFIX } from "shared/constants";
import { ensurePrefix } from "shared/helpers/string";
import type {
  SocialMentionWithSyndication,
  SocialSyndication,
} from "shared/types/mentions";

import SyndicationsTable from "./SyndicationsTable.vue";
import { type SyndicationPopperProps } from "./types";
import { useSyndicationsObserver } from "./useSyndicationsObserver";
import { useSyndicationsPopper } from "./useSyndicationsPopper";

const props = withDefaults(
  defineProps<SyndicationPopperProps<SocialMentionWithSyndication>>(),
  {
    parentElRef: null,
  }
);

const emit = defineEmits<{
  (e: "syndication-clicked", syndication: SocialSyndication): void;
}>();

const { isOpen, toggleOpen, abortController } = useSyndicationsObserver();

const { popperAttrs, syndications } = useSyndicationsPopper<
  SocialMentionWithSyndication,
  SocialSyndication
>({
  stream: props.stream,
  mention: props.mention,
  abortController,
});

const handleToggle = () => {
  toggleOpen(props.parentElRef);
};

const label = (syndication: SocialSyndication) => {
  const isHashtag = !syndication.source_name;

  const handleValue =
    "handle" in syndication ? syndication.handle : syndication.author;

  const handle = ensurePrefix(
    handleValue,
    isHashtag ? SOCIAL_PREFIX.hashtag : SOCIAL_PREFIX.handle
  );

  return {
    isHashtag,
    handle,
    sourceName: syndication.source_name,
  };
};

const syndicationClicked = (syndication: SocialSyndication) => {
  emit("syndication-clicked", syndication);
  isOpen.value = false;
};

defineExpose({ isOpen, handleToggle });
</script>
